import { create } from "zustand";
import { User } from "@/models/auth/User";
import { fetchInstance } from "@/utils/fetchInstance";
import {
  getAccessToken,
  getRefreshToken,
  setAccessToken,
  setRefreshToken,
} from "@/utils/tokenManager";
import { JwtCallbackResponse } from "@/models/auth/Jwt";
import { getSignupAttribution } from "@/features/analytics";

interface Tokens {
  access_token: string;
  refresh_token?: string;
}

interface ProfileState {
  profile: User | null;
  loading: boolean;
  initialLoading: boolean;
  tokens: Tokens | null;
  setProfile: (profile: User | null) => void;
  setTokens: (tokens: Tokens | null) => void;
  setLoading: (loading: boolean) => void;
  setInitialLoading: (initialLoading: boolean) => void;
  sendInitData: (initData: string) => Promise<void>;
  sendMobile: (initData: string) => Promise<SendMobileResult>;
  loadCurrentUser: () => Promise<void>;
}


type SendMobileResult = "UPDATED" | "ERROR" | "CANCELED";
type EitaaContactPayload = string | { response?: string };
const getContactResponse = (contactData?: unknown): string => {
  const normalized = contactData as EitaaContactPayload | undefined;
  if (typeof normalized === "string") return normalized;
  return normalized?.response ?? "";
};

// Hydrate from localStorage so a returning user is authenticated before the
// init-data round-trip finishes.
const readStoredTokens = (): Tokens | null => {
  const access = getAccessToken();
  if (!access) return null;
  return { access_token: access, refresh_token: getRefreshToken() ?? undefined };
};

export const useProfileStore = create<ProfileState>((set, get) => ({
  profile: null,
  loading: false,
  initialLoading: true,
  tokens: readStoredTokens(),
  setProfile: (profile) => set({ profile }),
  // localStorage is the single source of truth; keep it and the store in lockstep.
  setTokens: (tokens) => {
    setAccessToken(tokens?.access_token ?? null);
    setRefreshToken(tokens?.refresh_token ?? null);
    set({ tokens });
  },
  setLoading: (loading) => set({ loading }),
  setInitialLoading: (initialLoading) => set({ initialLoading }),

  sendInitData: async (initData: string) => {
    const { loading, profile } = get();
    // Idempotency guard: React StrictMode double-invokes effects in dev and
    // the provider can remount. Skip if a request is already in flight or we
    // are already authenticated, so `auth/jwt/callback` fires at most once.
    if (loading || profile) {
      return;
    }

    set({ loading: true });
    try {
      // Pass attribution so the backend can set the user's write-once
      // first-touch (from amz_utm_first) and stitch prior anonymous events to
      // the user via anonymous_id.
      const res = await fetchInstance<JwtCallbackResponse>(
        "auth/jwt/callback",
        {
          method: "POST",
          body: { eitaa_data: initData, ...getSignupAttribution() },
        }
      );

      if (res.data) {
        get().setTokens({
          access_token: res.data.access_token,
          refresh_token: res.data.refresh_token,
        });
        set({ profile: res.data.user });
      }
    } finally {
      set({ loading: false, initialLoading: false });
    }
  },

  // Web (non-Eitaa) sessions: a returning user has a token in localStorage but
  // no profile in memory, and `sendInitData` never runs (no Eitaa init data).
  // Rehydrate the profile from the stored token via /auth/me. fetchInstance
  // transparently refreshes the access token on a 401; only a hard failure
  // means the session is truly dead, so we clear it.
  loadCurrentUser: async () => {
    const { loading, profile, tokens } = get();
    if (loading || profile || !tokens?.access_token) {
      set({ initialLoading: false });
      return;
    }

    set({ loading: true });
    try {
      const res = await fetchInstance<User>("auth/me", {
        method: "GET",
        auth: true,
      });
      if (res.success && res.data) {
        set({ profile: res.data });
      } else {
        get().setTokens(null);
      }
    } catch {
      // Network error — keep the token; the user can retry on next load.
    } finally {
      set({ loading: false, initialLoading: false });
    }
  },

  sendMobile: async (initData) => {
    const webApp = window.Eitaa?.WebApp;
    if (!webApp) {
      return "ERROR";
    }

    return new Promise<SendMobileResult>((resolve) => {
      webApp.requestContact(async (isOk: boolean, contactData?: unknown) => {
        if (!isOk) {
          resolve("CANCELED");
          return;
        }

        const normalizedContactData = getContactResponse(contactData);
        if (!normalizedContactData) {
          resolve("CANCELED");
          return;
        }

        try {
          const res = await fetchInstance<
            User,
            { contact_data: string; eitaa_data: string }
          >("auth/jwt/link-phone", {
            method: "POST",
            auth: true,
            body: {
              contact_data: normalizedContactData,
              eitaa_data: initData,
            },
          });

          const updatedUser = res.data;
          if (updatedUser) {
            const { profile } = get();
            set({ profile: { ...(profile ?? ({} as User)), ...updatedUser } });
            resolve("UPDATED");
          } else {
            resolve("ERROR");
          }
        } catch {
          resolve("ERROR");
        }
      });
    });
  },

}));
